import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Upload, Button, Typography, message, Descriptions, Table, Space, Spin, Alert, Tag, Result,
} from 'antd';
import {
  UploadOutlined, FileAddOutlined, EyeOutlined, CheckCircleOutlined, InboxOutlined,
  CloseCircleOutlined, FileOutlined,
} from '@ant-design/icons';
import type { UploadFile as AntUploadFile } from 'antd/es/upload/interface';
import { protocolAPI } from '../services/api';
import { gray, state } from '../theme/tokens';

const { Dragger } = Upload;

interface HwpxUploadPageProps {
  onClose?: () => void;
  onEditNavigate?: (id: number) => void;
}

interface UploadResultItem {
  filename: string;
  success: boolean;
  data?: any;
  error?: string;
}

const HwpxUploadPage: React.FC<HwpxUploadPageProps> = ({ onClose, onEditNavigate }) => {
  const [fileList, setFileList] = useState<AntUploadFile[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState('');
  const [previewData, setPreviewData] = useState<any>(null);
  const [uploadResults, setUploadResults] = useState<UploadResultItem[] | null>(null);
  const navigate = useNavigate();

  const isModal = Boolean(onClose);
  const isSingle = fileList.length === 1;

  const handlePreview = async () => {
    if (!isSingle || !fileList[0].originFileObj) return;
    setPreviewing(true);
    setPreviewData(null);
    try {
      const { data } = await protocolAPI.previewHwpx(fileList[0].originFileObj as File);
      setPreviewData(data.parsed);
      message.success('파일 분석이 완료되었습니다.');
    } catch (e: any) {
      message.error(e.response?.data?.detail || '파일 분석에 실패했습니다.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleUpload = async () => {
    const files = fileList.filter(f => f.originFileObj);
    if (files.length === 0) return;
    setUploading(true);
    const results: UploadResultItem[] = [];
    for (const f of files) {
      setUploadingFile(f.name);
      try {
        const { data } = await protocolAPI.uploadHwpx(f.originFileObj as File);
        results.push({ filename: f.name, success: true, data });
      } catch (e: any) {
        results.push({ filename: f.name, success: false, error: e.response?.data?.detail || '등록 실패' });
      }
    }
    setUploading(false);
    setUploadingFile('');
    setUploadResults(results);
    const successCount = results.filter(r => r.success).length;
    if (successCount > 0) {
      message.success(`${successCount}건 등록 완료${results.length > successCount ? `, ${results.length - successCount}건 실패` : ''}`);
    } else {
      message.error('모든 파일 등록에 실패했습니다.');
    }
  };

  const handleReset = () => {
    setFileList([]);
    setPreviewData(null);
    setUploadResults(null);
  };

  const handleGoList = () => {
    if (isModal) onClose!();
    else navigate('/protocols');
  };

  const handleGoEdit = (id: number) => {
    if (isModal) { onClose!(); onEditNavigate?.(id); }
    else navigate(`/protocols/${id}/edit`);
  };

  const treatmentColumns = [
    { title: '시험약제', dataIndex: 'pesticide_name', key: 'pesticide_name', ellipsis: true },
    { title: '주성분함량(%)', dataIndex: 'active_ingredient_pct', key: 'pct', width: 120,
      render: (v: any) => (v ?? '') !== '' ? String(v) : '-' },
    { title: '희석배수', dataIndex: 'dilution_rate', key: 'dilution', width: 100,
      render: (v: any) => v || '-' },
    { title: '처리시기', dataIndex: 'treatment_timing', key: 'timing', ellipsis: true,
      render: (v: any) => v || '-' },
    { title: '기준량', dataIndex: 'standard_amount', key: 'std', width: 80,
      render: (v: any) => v || '-' },
    { title: '배량', dataIndex: 'double_amount', key: 'dbl', width: 80,
      render: (v: any) => v || '-' },
    { title: '시험년차', dataIndex: 'test_year_number', key: 'yr', width: 80,
      render: (v: any) => v != null ? String(v) : '-' },
    { title: '의뢰회사', dataIndex: 'client_company', key: 'company', width: 100,
      render: (v: any) => v || '-' },
  ];

  const plotColumns = [
    { title: '구분', dataIndex: 'category', key: 'category', width: 80 },
    { title: '처리수', dataIndex: 'treatment_count', key: 'tc', width: 80 },
    { title: '반복수', dataIndex: 'replication_count', key: 'rc', width: 80,
      render: (v: any) => v != null ? String(v) : '-' },
    { title: '총구수', dataIndex: 'total_plots', key: 'tp', width: 80,
      render: (v: any) => v != null ? String(v) : '-' },
    { title: '구당주수', dataIndex: 'trees_per_plot', key: 'tpp', width: 100 },
    { title: '소요주수', dataIndex: 'required_trees', key: 'rt', width: 100 },
    { title: '총소요주수', dataIndex: 'total_required_trees', key: 'trt', width: 100 },
  ];

  const treatments = previewData?.treatments || [];
  const plotDesigns = previewData?.plot_designs || [];

  // ── 등록 완료 화면 ──
  if (uploadResults) {
    const successItems = uploadResults.filter(r => r.success);
    const failItems = uploadResults.filter(r => !r.success);
    return (
      <Result
        status={successItems.length > 0 ? 'success' : 'error'}
        title={`${successItems.length}건 등록 완료${failItems.length > 0 ? `, ${failItems.length}건 실패` : ''}`}
        subTitle={
          <div style={{ textAlign: 'left', maxWidth: 640, margin: '0 auto' }}>
            {successItems.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <CheckCircleOutlined style={{ color: state.success, flexShrink: 0 }} />
                <span style={{ fontSize: 13 }}>
                  <strong>{r.data?.protocol_code || '-'}</strong>
                  {' · '}
                  {r.data?.title || r.filename}
                </span>
                <Button size="small" type="link" style={{ padding: 0 }}
                  onClick={() => handleGoEdit(r.data.id)}>
                  편집
                </Button>
              </div>
            ))}
            {failItems.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <CloseCircleOutlined style={{ color: state.error, flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13 }}>
                  <strong>{r.filename}</strong>
                  {' — '}
                  <span style={{ color: state.error }}>{r.error}</span>
                </span>
              </div>
            ))}
          </div>
        }
        extra={[
          <Button key="list" onClick={handleGoList}>목록으로</Button>,
          <Button key="new" onClick={handleReset}>다른 파일 업로드</Button>,
        ]}
      />
    );
  }

  const content = (
    <div>
      <Alert
        message="HWP / HWPX 파일을 업로드하면 자동으로 내용을 분석하여 시험계획서로 등록합니다."
        description="여러 파일을 한 번에 드래그하여 일괄 등록할 수 있습니다. 단일 파일 선택 시 미리보기로 파싱 결과를 확인할 수 있습니다."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Dragger
        fileList={fileList}
        multiple
        beforeUpload={(file) => {
          const lower = file.name.toLowerCase();
          if (!lower.endsWith('.hwpx') && !lower.endsWith('.hwp')) {
            message.error(`${file.name}: HWP 또는 HWPX 파일만 업로드할 수 있습니다.`);
            return Upload.LIST_IGNORE;
          }
          setFileList(prev => [
            ...prev.filter(f => f.uid !== file.uid),
            { ...file, uid: file.uid, name: file.name, originFileObj: file } as any,
          ]);
          setPreviewData(null);
          setUploadResults(null);
          return false;
        }}
        onRemove={(file) => {
          setFileList(prev => prev.filter(f => f.uid !== file.uid));
          setPreviewData(null);
        }}
        accept=".hwpx,.hwp"
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">클릭하거나 파일을 이 영역으로 드래그하세요</p>
        <p className="ant-upload-hint">.hwp · .hwpx 파일 지원 · 여러 파일 동시 선택 가능</p>
      </Dragger>

      {fileList.length > 1 && (
        <div style={{ marginTop: 12, padding: '8px 12px', background: gray[50], borderRadius: 6, border: '1px solid #e2e8f0' }}>
          {fileList.map(f => (
            <div key={f.uid} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 2 }}>
              <FileOutlined style={{ color: '#6366f1' }} />
              <span>{f.name}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'center' }}>
        <Button
          icon={<EyeOutlined />}
          onClick={handlePreview}
          loading={previewing}
          disabled={!isSingle || fileList.length === 0}
          size="large"
          title={fileList.length > 1 ? '미리보기는 파일 1개 선택 시만 가능합니다' : ''}
        >
          미리보기{fileList.length > 1 ? ' (단일만)' : ''}
        </Button>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleUpload}
          loading={uploading}
          disabled={fileList.length === 0}
          size="large"
        >
          {fileList.length > 1 ? `${fileList.length}건 일괄 등록` : '시험계획서로 등록'}
        </Button>
      </div>

      {uploading && (
        <div style={{ marginTop: 24, textAlign: 'center', padding: '24px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#64748b' }}>
            {uploadingFile ? `"${uploadingFile}" 등록 중...` : '등록 중...'}
            {fileList.length > 1 && (
              <span style={{ marginLeft: 8, fontSize: 12 }}>
                ({fileList.findIndex(f => f.name === uploadingFile) + 1} / {fileList.length})
              </span>
            )}
          </div>
        </div>
      )}

      {previewing && (
        <div style={{ marginTop: 24, textAlign: 'center', padding: '32px 0' }}>
          <Spin size="large" tip="파일 분석 중..." />
        </div>
      )}

      {previewData && !previewing && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <CheckCircleOutlined style={{ color: state.success, fontSize: 16 }} />
            <Typography.Text strong>파싱 결과 미리보기</Typography.Text>
            <Tag color="green">분석 완료</Tag>
          </div>

          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 3 }} size="small">
            <Descriptions.Item label="프로토콜 코드">{previewData.protocol_code || '-'}</Descriptions.Item>
            <Descriptions.Item label="항목명" span={2}>{previewData.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="시험기관">{previewData.institution || '-'}</Descriptions.Item>
            <Descriptions.Item label="시험년도">{previewData.test_year || '-'}</Descriptions.Item>
            <Descriptions.Item label="담당자">{previewData.manager || '-'}</Descriptions.Item>
            <Descriptions.Item label="실험예정시기">{previewData.planned_start || '-'}</Descriptions.Item>
            <Descriptions.Item label="실험종료시기">{previewData.planned_end || '-'}</Descriptions.Item>
            <Descriptions.Item label="시험장소">{previewData.test_location || '-'}</Descriptions.Item>
            <Descriptions.Item label="대상병해">{previewData.target_disease || '-'}</Descriptions.Item>
            <Descriptions.Item label="학명">{previewData.target_disease_scientific || '-'}</Descriptions.Item>
            <Descriptions.Item label="시험작물">{previewData.test_crop || '-'}</Descriptions.Item>
            <Descriptions.Item label="배치법">{previewData.plot_design || '-'}</Descriptions.Item>
            <Descriptions.Item label="반복수">{previewData.replications || '-'}</Descriptions.Item>
            <Descriptions.Item label="품종(약효)">{previewData.test_variety_efficacy || '-'}</Descriptions.Item>
          </Descriptions>

          {previewData.purpose ? (
            <div style={{ marginTop: 20 }}>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>1. 시험목적</Typography.Title>
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', padding: '12px 16px', background: gray[50], borderRadius: 8, border: '1px solid #f0f0f0' }}>
                {previewData.purpose}
              </Typography.Paragraph>
            </div>
          ) : null}

          <div style={{ marginTop: 20 }}>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              2. 시험방법 - 처리내용
              <Tag color="blue" style={{ marginLeft: 8 }}>{treatments.length}건</Tag>
            </Typography.Title>
            {treatments.length > 0 ? (
              <Table dataSource={treatments.map((t: any, i: number) => ({ ...t, key: i }))}
                columns={treatmentColumns} pagination={false} size="small" scroll={{ x: 800 }} bordered />
            ) : (
              <Typography.Text type="secondary">파싱된 처리내용이 없습니다.</Typography.Text>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              3. 시험구 배치 및 면적
              <Tag color="blue" style={{ marginLeft: 8 }}>{plotDesigns.length}건</Tag>
            </Typography.Title>
            {plotDesigns.length > 0 ? (
              <Table dataSource={plotDesigns.map((p: any, i: number) => ({ ...p, key: i }))}
                columns={plotColumns} pagination={false} size="small" bordered />
            ) : (
              <Typography.Text type="secondary">파싱된 시험구 배치 정보가 없습니다.</Typography.Text>
            )}
          </div>

          {previewData.survey_items ? (
            <div style={{ marginTop: 20 }}>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>4. 주요조사 항목</Typography.Title>
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', padding: '12px 16px', background: gray[50], borderRadius: 8, border: '1px solid #f0f0f0' }}>
                {previewData.survey_items}
              </Typography.Paragraph>
            </div>
          ) : null}

          <div style={{ marginTop: 28, textAlign: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
            <Space>
              <Button type="primary" icon={<UploadOutlined />} onClick={handleUpload} loading={uploading} size="large">
                이 내용으로 시험계획서 등록
              </Button>
              <Button onClick={handleReset}>초기화</Button>
            </Space>
          </div>
        </div>
      )}
    </div>
  );

  if (!isModal) {
    return (
      <div>
        <Card
          title={
            <Typography.Title level={4} style={{ margin: 0 }}>
              <FileAddOutlined style={{ marginRight: 8 }} />
              HWP / HWPX 파일 업로드
            </Typography.Title>
          }
        >
          {content}
        </Card>
      </div>
    );
  }

  return content;
};

export default HwpxUploadPage;
